import { AUTH_MODE, AuthMode, DEFAULT_API_PREFIX, EMPTY_STRING } from "src/components/constant/constant";

export type SSOProvider = {
  label: string;
  providerName: string;
};

export type CognitoConfig = {
  baseUrl: string;
  redirectUrl: string;
  oauthProvider: string;
  googleOAuthUrl: string;
  ssoProviders?: SSOProvider[];
  logoutUrl?: string;
};

export type AppConfig = {
  API_URL: string;
  COGNITO_USER_POOL_ID: string;
  COGNITO_CLIENT_ID: string;
  INTERNAL_API_KEY: string;
  cognitoConfig: CognitoConfig;
  S3_ASSET_BUCKET: string;
  S3_FILE_BUCKET: string;
  REGION: string;
  IDENTITY_POOL_ID: string;
  PARTNER_ID: string;
  TEXT_EDITOR_KEY: string;
  SHOW_SSO_LOGIN: boolean;
  FIREBASE_CONFIG: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId: string;
  };
  XERO_CONFIG: {
    clientId: string;
    redirect_url: string;
    scopes: string;
  };
  POSTHOG_API_KEY: string;
  POSTHOG_HOST: string;
};

type HostConfig = {
  clientId: string;
  redirectUrl: string;
  showSSOLogin: boolean;
  ssoProviders?: SSOProvider[];
  logoutUrl?: string;
};

// Malformed or empty JSON disables the tenant-picker feature rather than breaking sign-in.
function parseSSOProviders(raw?: string): SSOProvider[] | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) return undefined;
    const providers = parsed.filter((entry) => entry?.label && entry?.providerName);

    return providers.length ? providers : undefined;
  } catch {
    return undefined;
  }
}

let cachedHeaders: HostConfig | null = null;

async function loadConfigFromEnvironment(): Promise<HostConfig> {
  if (cachedHeaders) return cachedHeaders;

  const hostname = window.location.hostname;
  const isLocal = hostname === "localhost" || hostname === "127.0.0.1";

  let clientId = EMPTY_STRING;
  let redirectUrl = EMPTY_STRING;
  let showSSOLogin = false;
  let ssoProviders: SSOProvider[] | undefined;
  let logoutUrl: string | undefined;

  if (isLocal) {
    clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || EMPTY_STRING;
    redirectUrl = process.env.REACT_APP_COGNITO_REDIRECT_URL || EMPTY_STRING;
    showSSOLogin = process.env.REACT_APP_SHOW_SSO_LOGIN === "true";
    ssoProviders = parseSSOProviders(process.env.REACT_APP_SSO_PROVIDERS);
    logoutUrl = process.env.REACT_APP_LOGOUT_URL || undefined;
  } else {
    // Amplify env var names can't contain "-", so dhs-sa reads REACT_APP_DHS_SA_*.
    const hostPrefix = hostname.split(".gimmeit.net.au")[0].toUpperCase().replace(/-/g, "_");

    const clientIdEnvVar = `REACT_APP_${hostPrefix}_CLIENT_ID`;
    const redirectUrlEnvVar = `REACT_APP_${hostPrefix}_REDIRECT_URL`;
    const showSsoLoginEnvVar = `REACT_APP_${hostPrefix}_SHOW_SSO_LOGIN`;
    const ssoProvidersEnvVar = `REACT_APP_${hostPrefix}_SSO_PROVIDERS`;
    const logoutUrlEnvVar = `REACT_APP_${hostPrefix}_LOGOUT_URL`;

    clientId = process.env[clientIdEnvVar] || EMPTY_STRING;
    redirectUrl = process.env[redirectUrlEnvVar] || EMPTY_STRING;
    showSSOLogin = process.env[showSsoLoginEnvVar] === "true" || false;
    ssoProviders = parseSSOProviders(process.env[ssoProvidersEnvVar]);
    logoutUrl = process.env[logoutUrlEnvVar] || undefined;
  }

  cachedHeaders = {
    clientId,
    redirectUrl,
    showSSOLogin,
    ssoProviders,
    logoutUrl
  };

  return cachedHeaders;
}

let config: AppConfig | null = null;

// Read straight from the environment so module-level code (store transforms) can branch before loadConfig runs.
const readAuthMode = (): AuthMode =>
  process.env.REACT_APP_AUTH_MODE === AUTH_MODE.COOKIE ? AUTH_MODE.COOKIE : AUTH_MODE.TOKEN;

export const isCookieAuthMode = () => readAuthMode() === AUTH_MODE.COOKIE;

// Sandbox and production share one Amplify app, so each branch reaches the BFF through its own path prefix.
const readApiPrefix = (): string => {
  const prefix = (process.env.REACT_APP_API_PREFIX || EMPTY_STRING).trim().replace(/\/+$/, EMPTY_STRING);

  return prefix || DEFAULT_API_PREFIX;
};

export const API_PREFIX = readApiPrefix();

export async function loadConfig(): Promise<void> {
  const { clientId, redirectUrl, showSSOLogin, ssoProviders, logoutUrl } = await loadConfigFromEnvironment();

  config = {
    API_URL: process.env.REACT_APP_API_URL || EMPTY_STRING,
    COGNITO_USER_POOL_ID: process.env.REACT_APP_COGNITO_USER_POOL_ID || EMPTY_STRING,
    COGNITO_CLIENT_ID: clientId,
    INTERNAL_API_KEY: process.env.REACT_APP_INTERNAL_API_KEY || EMPTY_STRING,
    S3_ASSET_BUCKET: process.env.REACT_APP_S3_ASSET_BUCKET || EMPTY_STRING,
    S3_FILE_BUCKET: process.env.REACT_APP_S3_FILE_BUCKET || EMPTY_STRING,
    REGION: process.env.REACT_APP_REGION || EMPTY_STRING,
    IDENTITY_POOL_ID: process.env.REACT_APP_IDENTITY_POOL_ID || EMPTY_STRING,
    PARTNER_ID: process.env.REACT_APP_PARTNER_ID || EMPTY_STRING,
    TEXT_EDITOR_KEY: process.env.REACT_APP_TEXT_EDITOR_KEY || EMPTY_STRING,
    SHOW_SSO_LOGIN: showSSOLogin,
    FIREBASE_CONFIG: {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY || EMPTY_STRING,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || EMPTY_STRING,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || EMPTY_STRING,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || EMPTY_STRING,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || EMPTY_STRING,
      appId: process.env.REACT_APP_FIREBASE_APP_ID || EMPTY_STRING,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || EMPTY_STRING
    },
    XERO_CONFIG: {
      clientId: process.env.REACT_APP_XERO_CLIENT_ID || EMPTY_STRING,
      redirect_url: process.env.REACT_APP_XERO_REDIRECT_URL || EMPTY_STRING,
      scopes: process.env.REACT_APP_XERO_SCOPES || EMPTY_STRING
    },
    POSTHOG_API_KEY: process.env.REACT_APP_POSTHOG_API_KEY || EMPTY_STRING,
    POSTHOG_HOST: process.env.REACT_APP_POSTHOG_HOST || EMPTY_STRING,
    cognitoConfig: {
      baseUrl: process.env.REACT_APP_COGNITO_BASE_URL || EMPTY_STRING,
      redirectUrl,
      oauthProvider:
        `${process.env.REACT_APP_COGNITO_BASE_URL}/oauth2/authorize?client_id=${clientId}&response_type=code&redirect_uri=${encodeURI(redirectUrl)}` ||
        EMPTY_STRING,
      googleOAuthUrl:
        `${process.env.REACT_APP_COGNITO_BASE_URL}/oauth2/authorize?identity_provider=Google&redirect_uri=${encodeURI(redirectUrl)}&response_type=code&client_id=${clientId}&scope=email+profile+openid` ||
        EMPTY_STRING,
      ssoProviders,
      logoutUrl
    }
  };
}

export function getConfig(): AppConfig {
  if (!config) throw new Error("Config not loaded yet");
  return config;
}
